/**
 * ExamplePerformancesPanel — athlete-facing "Example performances" section
 * on the template read view (backlog/example-performances-template-
 * resources.md, PR 1+2, owner-approved 2026-09-23).
 *
 * Rendered inside TemplateDetails.tsx for EVERYONE with template read
 * access (coach and athlete alike) — distinct from the coach-only
 * CalibrationTakesPanel (management UI) and CalibrationTakeEditForm's
 * AthleteVisibilityControl (the opt-in toggle).
 *
 * Contract:
 *  - Lists ONLY takes the backend already filtered to `is_athlete_visible
 *    AND consent_given_at IS NOT NULL AND status='completed'`
 *    (GET .../calibration-takes/visible) — this component does no ACL
 *    filtering of its own.
 *  - Label-only ranking, NO numeric grade or score anywhere in this panel
 *    (owner ruling: "athletes never see letters").
 *  - Each item's video is served ONLY via a signed URL from
 *    `useCalibrationTakeMediaUrl` — the `<video>` element never renders
 *    until the signed token resolves. An unsigned stream URL is never
 *    referenced anywhere in this file.
 *  - Empty state: renders NOTHING (no "no examples" noise) for an athlete.
 *    A coach sees a short, quiet hint instead, so the feature isn't
 *    invisible to the person who would enable it.
 *  - Grouping: two epic-defined display groups — "Good examples" (label
 *    'good') and "Common mistakes" (label 'bad' or 'fail') — see backlog/
 *    example-performances-template-resources.md's Frontend section. Each
 *    item's own chip uses the same three-way vocabulary already used
 *    elsewhere in the coach UI (CalibrationTakeList/EditForm): 'Correct' /
 *    'Flawed' / 'Disqualifying' — one vocabulary, not a second one invented
 *    for this panel.
 *
 * Hooks-in-loops rule: TakeVideo below is its OWN component so
 * `useCalibrationTakeMediaUrl` is called once per item by a component
 * instance, never conditionally or inside a loop body directly.
 */

import { Card, CardContent, CardHeader, CardTitle, Spinner } from '@/components/ui';
import Badge from '@/components/ui/Badge';
import { useVisibleExamplePerformances } from '@/hooks/useCalibrationTakes';
import { useCalibrationTakeMediaUrl } from '@/hooks/useMediaUrl';
import type { CalibrationTakeLabel, ExamplePerformance } from '@/types/calibrationTake';

const LABEL_CHIP: Record<
  CalibrationTakeLabel,
  { text: string; variant: 'success' | 'warning' | 'error' }
> = {
  good: { text: 'Correct', variant: 'success' },
  bad: { text: 'Flawed', variant: 'warning' },
  fail: { text: 'Disqualifying', variant: 'error' },
};

const GROUPS: { key: 'good' | 'mistakes'; title: string; labels: CalibrationTakeLabel[] }[] = [
  { key: 'good', title: 'Good examples', labels: ['good'] },
  { key: 'mistakes', title: 'Common mistakes', labels: ['bad', 'fail'] },
];

interface ExamplePerformancesPanelProps {
  templateId: string;
  /** True for a coach/admin viewer — controls the empty-state behaviour. */
  isCoach: boolean;
}

// ---------------------------------------------------------------------------
// Single item — video via signed URL, quote-styled note, label chip. No
// numbers anywhere.
// ---------------------------------------------------------------------------

function ExamplePerformanceVideo({
  templateId,
  takeId,
}: {
  templateId: string;
  takeId: string;
}) {
  // Per-item component call — see module docstring "Hooks-in-loops rule".
  const { streamUrl, isLoading } = useCalibrationTakeMediaUrl(templateId, takeId);

  if (streamUrl) {
    return (
      <video
        data-testid={`example-performance-video-${takeId}`}
        src={streamUrl}
        controls
        preload="metadata"
        className="w-full rounded-lg border border-border-subtle bg-black mt-2"
      />
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-4" data-testid={`example-performance-video-loading-${takeId}`}>
        <Spinner size="sm" label="Loading video..." />
      </div>
    );
  }

  return null;
}

function ExamplePerformanceItem({
  templateId,
  performance,
}: {
  templateId: string;
  performance: ExamplePerformance;
}) {
  const chip = LABEL_CHIP[performance.label];

  return (
    <div
      className="rounded-lg border border-border-subtle bg-dark-elevated-1 p-3"
      data-testid={`example-performance-${performance.take_id}`}
    >
      <Badge variant={chip.variant} size="sm">
        {chip.text}
      </Badge>

      {performance.athlete_note && (
        <div className="px-3 py-2.5 rounded-md bg-dark-base/50 border-l-2 border-l-accent/40 mt-2">
          <p className="text-xs text-text-secondary leading-relaxed italic">
            &ldquo;{performance.athlete_note}&rdquo;
          </p>
        </div>
      )}

      {performance.has_video && (
        <ExamplePerformanceVideo templateId={templateId} takeId={performance.take_id} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Panel
// ---------------------------------------------------------------------------

export default function ExamplePerformancesPanel({
  templateId,
  isCoach,
}: ExamplePerformancesPanelProps) {
  const { data, isLoading, error } = useVisibleExamplePerformances(templateId);
  const performances = data?.performances ?? [];

  // Still loading: render nothing rather than a flash of an empty-state card.
  if (isLoading) return null;

  // A load failure is treated like "nothing to show" for an athlete (no
  // scary error banner for a purely-supplementary section); a coach gets a
  // quiet note so a real problem isn't mistaken for "no examples yet".
  if (error) {
    if (!isCoach) return null;
    return (
      <Card>
        <CardHeader>
          <CardTitle>Example performances</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-text-muted" data-testid="example-performances-error">
            Could not load example performances right now.
          </p>
        </CardContent>
      </Card>
    );
  }

  if (performances.length === 0) {
    if (!isCoach) return null;
    return (
      <Card>
        <CardHeader>
          <CardTitle>Example performances</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-text-muted" data-testid="example-performances-coach-empty">
            No example performances shared with athletes yet. Turn on
            &ldquo;Athlete-visible&rdquo; on a take below to add one.
          </p>
        </CardContent>
      </Card>
    );
  }

  const grouped = GROUPS.map((group) => ({
    ...group,
    items: performances.filter((p) => group.labels.includes(p.label)),
  })).filter((group) => group.items.length > 0);

  return (
    <Card data-testid="example-performances-panel">
      <CardHeader>
        <CardTitle>Example performances</CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        {grouped.map((group) => (
          <div key={group.key} data-testid={`example-performances-group-${group.key}`}>
            <p className="text-sm font-medium text-text-secondary mb-2">{group.title}</p>
            <div className="space-y-3">
              {group.items.map((performance) => (
                <ExamplePerformanceItem
                  key={performance.take_id}
                  templateId={templateId}
                  performance={performance}
                />
              ))}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
